/**
 * millionsAudio — Web Audio suspense engine for Plyx Millions.
 * Fully synthesized (no audio files, no licensing): a low drone + heartbeat
 * that speeds up as the clock drains, countdown ticks, and win/lose stingers.
 */
const MUTE_KEY = "plyx_millions_muted";

class MillionsAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.droneNodes = [];
    this.droneGain = null;
    this.droneFilter = null;
    this.beatTimer = null;
    this.intensity = 0;
    this.playing = false;
    this.muted = typeof localStorage !== "undefined" && localStorage.getItem(MUTE_KEY) === "1";
  }

  ensure() {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    if (!this.ctx) {
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.5;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
    return true;
  }

  setMuted(m) {
    this.muted = m;
    try { localStorage.setItem(MUTE_KEY, m ? "1" : "0"); } catch { /* private mode */ }
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.5, this.ctx.currentTime, 0.05);
  }

  toggleMuted() { this.setMuted(!this.muted); return this.muted; }

  startSuspense() {
    if (!this.ensure() || this.playing) return;
    this.playing = true;
    const t = this.ctx.currentTime;
    this.droneGain = this.ctx.createGain();
    this.droneGain.gain.setValueAtTime(0.0001, t);
    this.droneGain.gain.linearRampToValueAtTime(0.05, t + 1.5);
    this.droneFilter = this.ctx.createBiquadFilter();
    this.droneFilter.type = "lowpass";
    this.droneFilter.frequency.value = 220;
    [55, 55.7, 110.4].forEach((f) => {
      const o = this.ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = f;
      o.connect(this.droneFilter);
      o.start();
      this.droneNodes.push(o);
    });
    this.droneFilter.connect(this.droneGain);
    this.droneGain.connect(this.master);
    this._scheduleBeat();
  }

  _scheduleBeat() {
    if (!this.playing) return;
    this._thump();
    setTimeout(() => { if (this.playing) this._thump(0.75); }, 180);
    const interval = (1.5 - this.intensity) * 1000; // 1.5s calm → 0.5s panic
    this.beatTimer = setTimeout(() => this._scheduleBeat(), Math.max(450, interval));
  }

  _thump(vel = 1) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(90 + this.intensity * 35, t);
    o.frequency.exponentialRampToValueAtTime(35, t + 0.22);
    g.gain.setValueAtTime(0.26 * vel * (0.6 + this.intensity * 0.7), t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + 0.3);
  }

  setIntensity(x) {
    this.intensity = Math.max(0, Math.min(1, x));
    if (this.droneFilter && this.ctx) {
      this.droneFilter.frequency.setTargetAtTime(220 + this.intensity * 950, this.ctx.currentTime, 0.3);
    }
  }

  tick(urgent) {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "square";
    o.frequency.value = urgent ? 1400 : 950;
    g.gain.setValueAtTime(urgent ? 0.12 : 0.07, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + 0.1);
  }

  stopMusic() {
    this.playing = false;
    clearTimeout(this.beatTimer);
    if (this.droneGain && this.ctx) {
      this.droneGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.2);
      const nodes = this.droneNodes;
      setTimeout(() => nodes.forEach((o) => { try { o.stop(); } catch { /* stopped */ } }), 800);
      this.droneNodes = [];
      this.droneGain = null;
      this.droneFilter = null;
    }
    this.intensity = 0;
  }

  _note(freq, t0, dur, type = "sine", vol = 0.16) {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g);
    g.connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.05);
  }

  sfxCorrect() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => this._note(f, t + i * 0.09, 0.35, "triangle", 0.18));
  }

  sfxWrong() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    [220, 174.6, 138.6].forEach((f, i) => this._note(f, t + i * 0.16, 0.5, "sawtooth", 0.11));
  }

  sfxWin() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5, 1318.5, 1568].forEach((f, i) => this._note(f, t + i * 0.12, 0.6, "triangle", 0.2));
    [261.6, 329.6, 392].forEach((f) => this._note(f, t + 0.75, 1.2, "sine", 0.12));
  }

  sfxCashout() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    [783.99, 987.77, 1174.66].forEach((f, i) => this._note(f, t + i * 0.1, 0.4, "triangle", 0.16));
  }
}

export const millionsAudio = new MillionsAudio();
